import get from 'lodash/get';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from "@mui/material/Stack";
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import SessionStatus from './SessionStatus';
import SessionActions from './SessionActions';

import {
  formatSessionDate,
  formatSessionLocation,
  formatSessionTime,
  formatSessionVirtualLink,
  getSessionLink
} from '../../helpers/sessions';
import config from "../../config/config";
import { formatProviderName } from '../../helpers/providers';
import { formatPatientName, formatPatientPhone } from '../../helpers/patients';
import { ClockIcon, BlackPersonIcon, PhoneIcon, PinIcon, LaunchIcon, PersonIcon, CalendarIcon } from '../../styles/icons';
import { MAX_WITH_MOBILE_MEDIA } from '../../config/getTheme';
import './SessionListItem.scss'

const SessionListItem = props => {
  const navigate = useNavigate();
  const { session: s, isPatient, onSave, isMobile, isDashboard } = props;
  const defaultLargeFont = isMobile ? 'largeSemiBoldTight' : 'subtitle2';
  const defaultSecondFont = 'large';
  if (!s) return <></>;

  const navigateSessionDetail = () => {
    navigate(getSessionLink(s.id));
  };

  const locationFormatted = s.location === 'VIRTUAL'
    ? formatSessionVirtualLink(s, { returnLinkText: true })
    : formatSessionLocation(s, { returnLinkText: true });

  const phoneFormatted = formatPatientPhone(get(s, 'patient'), { returnLinkText: true });
  const sessionActions = SessionActions({
    ...props,
    variant: 'card',
    variantText: isMobile ? 'smallSemiBoldTightInter' : 'large600TightInter',
    size: isMobile ? 'medium' : 'large',
    limit: 2,
    onSave
  });
  return (
    <Box className='SessionListItem mx-table__row'>
      <Box className='mx-table__cell'>
        <Stack direction={isMobile ? 'row' : 'column'} justifyContent={'space-between'}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <ClockIcon />
            {!isDashboard &&
              <Typography variant={defaultLargeFont} noWrap>
                {formatSessionDate(s)}
              </Typography>
            }
            <Typography variant={defaultLargeFont} noWrap sx={{ fontWeight: isMobile && !isDashboard ? 400 : 600 }}>
              {formatSessionTime(s)}
            </Typography>
          </Stack>

          {!isMobile
            ?
            <>
              {!isDashboard
                ?
                <Stack direction='row' alignItems='center' spacing={1}>
                  <BlackPersonIcon />
                  <Typography variant={defaultLargeFont} noWrap>
                    {
                      isPatient
                        ? formatProviderName(get(s, 'provider'))
                        : formatPatientName(get(s, 'patient'))
                    }
                  </Typography>
                </Stack>
                :
                <Stack direction='row' alignItems='center' spacing={1} className='SessionListItem__date-box'>
                  <CalendarIcon />
                  <Typography variant={defaultLargeFont}>
                    {formatSessionDate(s)}
                  </Typography>
                </Stack>
              }
            </>
            :
            <>
              <Stack direction='row' alignItems='center' justifyContent='end' spacing={2.5}>
                <SessionStatus session={s} />
                <Button onClick={navigateSessionDetail} sx={{ minWidth: 'auto', padding: 0 }}>
                  <LaunchIcon style={{ fill: config().colors.text, width: '0.9375rem', height: '0.9375rem' }} />
                </Button>
              </Stack>
            </>
          }
        </Stack>
      </Box>

      {isMobile && isDashboard &&
        <Box className='mx-table__cell'>
          <Stack direction='row' alignItems='center' spacing={1} className='SessionListItem__date-box'>
            <CalendarIcon />
            <Typography variant={defaultLargeFont}>
              {formatSessionDate(s)}
            </Typography>
          </Stack>
        </Box>
      }

      <Box className='mx-table__cell'>
        <Stack direction='row' alignItems='center' spacing={1}>
          <BlackPersonIcon />
          <Typography variant={defaultLargeFont} noWrap>
            {
              isPatient
                ? formatProviderName(get(s, 'provider'))
                : formatPatientName(get(s, 'patient'))
            }
          </Typography>
        </Stack>
      </Box>

      {!isPatient &&
        <Box className='mx-table__cell'>
          {phoneFormatted &&
            <Stack direction='row' alignItems='center' spacing={1}>
              <PhoneIcon />
              <Typography variant={defaultSecondFont} noWrap>
                {phoneFormatted}
              </Typography>
            </Stack>
          }
        </Box>
      }

      <Box className='mx-table__cell' style={{ display: isMobile && !locationFormatted ? 'none' : '' }}>
        {locationFormatted &&
          <Stack direction='row' alignItems='center' spacing={1}>
            <PinIcon />
            <Typography
              variant={defaultSecondFont}
              sx={{
                minWidth: '200px',
              }}
            >
              {locationFormatted}
            </Typography>
          </Stack>
        }
      </Box>

      {!isPatient && props.showAllProviders &&
        <Box className='mx-table__cell'>
          <Stack direction='row' alignItems='center' spacing={1}>
            <PersonIcon />
            <Typography variant={defaultSecondFont}>
              {`Provider: ${formatProviderName(get(s, 'provider'))}`}
            </Typography>
          </Stack>
        </Box>
      }

      <Box className='mx-table__cell mx-table__cell__actions'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
          className='SessionListItem__actions'
          spacing={1}
          sx={{
            '& button span': {
              [MAX_WITH_MOBILE_MEDIA]: {
                lineHeight: 'normal'
              },
            },
          }}
        >
          {sessionActions}
        </Stack>
      </Box>

      <Box
        className='mx-table__cell'
        sx={{
          display: isMobile ? 'none' : '',
          textAlign: 'right',
          width: '20px',
          paddingLeft: 0,
          paddingRight: '15px'
        }}
      >
        <Stack
          direction='column'
          alignItems='flex-end'
          justifyContent='center'
          spacing={1}
          style={{
            position: 'relative',
            top: '-6px',
          }}
        >
          <Button
            onClick={navigateSessionDetail}
            sx={{ minWidth: 'auto', paddingRight: 0 }}
          >
            <LaunchIcon style={{ width: '0.9375rem', height: '0.9375rem' }} />
          </Button>
          <SessionStatus session={s} />
        </Stack>
      </Box>
    </Box>
  );
};

export default SessionListItem;
